import React, {
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react";
import debounce from "lodash.debounce";
import Modal from "react-modal";
import SelectSearch from "react-select";
import { Checkbox, FormControlLabel } from "@material-ui/core";
import Loader from "../../common/Loader";
import ChabadHousesApi from "../../../services/resources/ChabadHousesApi";
import { formatFullName } from "../../../lib";
import axios from "axios";

const STUDENTS_PER_PAGE = 10;

export default function AddChatLocationModal({
  chabadHousesList,
  chabadHousesListErrorMessage,
  chabadHousesListLoading,
  close,
  submit,
}) {
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [newLocation, setNewLocation] = useState(null);

  // Shluchim state
  const [shluchim, setShluchim] = useState([]);
  const [shluchimLoading, setShluchimLoading] = useState(false);
  const [selectedShliachPersonIds, setSelectedShliachPersonIds] = useState([]);

  // Students state
  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(false);
  const [selectedStudentPersonIds, setSelectedStudentPersonIds] = useState([]);
  const [studentRecentLoginWeeks, setStudentRecentLoginWeeks] = useState(25);
  const [studentsExpanded, setStudentsExpanded] = useState(false);
  const [studentsPage, setStudentsPage] = useState(1);

  const apiSignalRef = useRef(axios.CancelToken.source());

  useEffect(() => {
    const _apiSignal = apiSignalRef.current;
    return () => _apiSignal.cancel();
  }, []);

  const fetchShluchim = useCallback(async (chabadHouseId) => {
    setShluchimLoading(true);
    try {
      const shluchimData = await ChabadHousesApi.getChabadHouseShluchim(
        apiSignalRef.current.token,
        chabadHouseId,
        true,
      );
      setShluchim(shluchimData);
      setShluchimLoading(false);
      // auto-select all shluchim
      setSelectedShliachPersonIds(shluchimData.map((s) => s.personID));
    } catch (err) {
      if (!axios.isCancel(err)) {
        setShluchimLoading(false);
      }
    }
  }, []);

  const fetchStudents = useCallback(async (chabadHouseId, recentWeeks) => {
    setStudentsLoading(true);
    try {
      const studentsData = await ChabadHousesApi.getChabadHouseStudents(
        apiSignalRef.current.token,
        chabadHouseId,
        { recentLoginWeeks: recentWeeks },
      );
      setStudents(studentsData);
      setStudentsLoading(false);
      setStudentsPage(1);
      setStudentsExpanded(false);
      // auto-select all students
      setSelectedStudentPersonIds(studentsData.map((s) => s.personID));
    } catch (err) {
      if (!axios.isCancel(err)) {
        setStudentsLoading(false);
      }
    }
  }, []);

  const onClose = useCallback(() => {
    // reset state
    setErrorMessage("");
    setLoading(false);
    setNewLocation(null);
    setShluchim([]);
    setSelectedShliachPersonIds([]);
    setStudents([]);
    setSelectedStudentPersonIds([]);
    setStudentRecentLoginWeeks(25);
    setStudentsExpanded(false);

    close();
  }, [close]);

  const onSelectChabadHouse = useCallback(
    (location) => {
      setErrorMessage("");
      setNewLocation(location);

      if (location) {
        fetchShluchim(location.value);
        fetchStudents(location.value, studentRecentLoginWeeks);
      } else {
        setShluchim([]);
        setSelectedShliachPersonIds([]);
        setStudents([]);
        setSelectedStudentPersonIds([]);
      }
    },
    [fetchShluchim, fetchStudents, studentRecentLoginWeeks],
  );

  const debouncedFetchStudents = useMemo(
    () =>
      debounce(
        (chabadHouseId, weeks) => fetchStudents(chabadHouseId, weeks),
        500,
      ),
    [fetchStudents],
  );

  const onRecentLoginWeeksChange = useCallback(
    (e) => {
      const weeks = e.target.value;
      setStudentRecentLoginWeeks(weeks);
      newLocation && debouncedFetchStudents(newLocation.value, weeks);
    },
    [newLocation, debouncedFetchStudents],
  );

  const toggleShliachSelection = useCallback((personId) => {
    setSelectedShliachPersonIds((prev) =>
      prev.includes(personId)
        ? prev.filter((id) => id !== personId)
        : [...prev, personId],
    );
  }, []);

  const toggleStudentSelection = useCallback((personId) => {
    setSelectedStudentPersonIds((prev) =>
      prev.includes(personId)
        ? prev.filter((id) => id !== personId)
        : [...prev, personId],
    );
  }, []);

  const selectAllStudents = useCallback(() => {
    setSelectedStudentPersonIds(students.map((s) => s.personID));
  }, [students]);

  const deselectAllStudents = useCallback(() => {
    setSelectedStudentPersonIds([]);
  }, []);

  const onSubmit = useCallback(async () => {
    if (!newLocation) {
      return;
    }

    if (selectedShliachPersonIds.length === 0) {
      setErrorMessage("At least one shliach must be selected");
      return;
    }

    setErrorMessage("");
    setLoading(true);

    const { error, errorMessage: errMsg } = await submit(newLocation.value, {
      memberShliachPersonIds: selectedShliachPersonIds,
      memberStudentPersonIds: selectedStudentPersonIds,
    });

    if (error) {
      setErrorMessage(errMsg);
      setLoading(false);
      return;
    }

    onClose();
  }, [
    newLocation,
    selectedShliachPersonIds,
    selectedStudentPersonIds,
    submit,
    onClose,
  ]);

  const paginatedStudents = students.slice(0, studentsPage * STUDENTS_PER_PAGE);
  const hasMoreStudents = paginatedStudents.length < students.length;

  return (
    <Modal isOpen={true} className="modal-container">
      <div className="modal-card card add-shliach-modal medium-modal">
        <p className="large-text fw-500 mb-16">
          Enroll Location in Chat Community
        </p>

        <div className="mb-24">
          <p className="medium-text fw-700 mb-8">Chabad House</p>
          <SelectSearch
            disabled={loading || chabadHousesListLoading}
            isClearable={true}
            isSearchable={true}
            onChange={onSelectChabadHouse}
            options={chabadHousesList.map((ch) => ({
              value: ch.chabadHouseID,
              label: `${ch.chabadHouseName} - ${ch.campusName} - ${[
                ch.shliachFirstName,
                ch.shluchaFirstName,
              ]
                .filter((n) => n)
                .join(" & ")} ${ch.shliachLastName}`,
            }))}
            placeholder={
              chabadHousesListLoading
                ? "Loading Locations..."
                : "Select Chabad House"
            }
            style={{
              height: "36px",
              width: "100%",
              border: "1px solid #edecec",
              fontSize: "14px",
            }}
            value={newLocation}
          />
          {chabadHousesListErrorMessage && (
            <p className="error-text">{chabadHousesListErrorMessage}</p>
          )}
        </div>

        {newLocation && (
          <>
            <div className="mb-24">
              <p className="medium-text fw-700 mb-8">
                Shluchim members ({selectedShliachPersonIds.length} selected)
              </p>
              <p className="small-text mb-8">
                Selected Shluchim will be added to the chat community. Shluchim
                are never automatically added to the chat community. If a new
                Shliach is added to an enrolled Chabad House, they can be
                manually added to the chat community from the Shluchim tab.
              </p>
              {shluchimLoading ? (
                <p className="small-text">Loading Shluchim...</p>
              ) : shluchim.length === 0 ? (
                <p className="small-text">No Shluchim found</p>
              ) : (
                <div className="chat-location-members-grid">
                  {shluchim.map((shliach) => (
                    <FormControlLabel
                      className="chat-member-checkbox"
                      control={
                        <Checkbox
                          checked={selectedShliachPersonIds.includes(
                            shliach.personID,
                          )}
                          disabled={loading}
                          onChange={() =>
                            toggleShliachSelection(shliach.personID)
                          }
                        />
                      }
                      key={shliach.personID}
                      label={shliach.fullName}
                    />
                  ))}
                </div>
              )}
            </div>

            <div className="mb-24">
              <p className="medium-text fw-700 mb-8">
                Student members ({selectedStudentPersonIds.length} selected)
              </p>
              <p className="small-text mb-8">
                Selected students will be added to the chat community. Students
                at an enrolled Chabad House who are not selected here will be
                automatically added to the chat community upon accessing the
                student app. Students can opt out of the chat community from
                their profile.
              </p>
              <div className="flex flex-align-center mb-8">
                <p className="small-text fw-700">
                  Include students who logged in within the last
                </p>
                <input
                  className="custom-input ml-8 mr-8"
                  disabled={loading}
                  min="0"
                  onChange={onRecentLoginWeeksChange}
                  style={{ width: "80px" }}
                  type="number"
                  value={studentRecentLoginWeeks}
                />
                <p className="small-text fw-700">weeks</p>
              </div>

              <div
                className="chat-location-students-header"
                onClick={() => setStudentsExpanded(!studentsExpanded)}
              >
                <span className="small-text">
                  {studentsLoading
                    ? "Loading students..."
                    : `${selectedStudentPersonIds.length} of ${students.length} students selected`}
                </span>
                <i className="material-icons">
                  {studentsExpanded ? "expand_less" : "expand_more"}
                </i>
              </div>

              {studentsExpanded && !studentsLoading && (
                <div className="chat-location-students-expanded">
                  {!students.length ? (
                    <p className="accent-text-secondary small-text">
                      No students found matching criteria
                    </p>
                  ) : (
                    <>
                      <div className="flex flex-justify-end mb-8">
                        <button
                          className="btn custom-btn btn-small btn-light mr-8"
                          onClick={selectAllStudents}
                          type="button"
                        >
                          Select All
                        </button>
                        <button
                          className="btn custom-btn btn-small btn-light"
                          onClick={deselectAllStudents}
                          type="button"
                        >
                          Deselect All
                        </button>
                      </div>
                      <div className="chat-location-members-grid">
                        {paginatedStudents.map((student) => (
                          <FormControlLabel
                            className="chat-member-checkbox"
                            control={
                              <Checkbox
                                checked={selectedStudentPersonIds.includes(
                                  student.personID,
                                )}
                                disabled={loading}
                                onChange={() =>
                                  toggleStudentSelection(student.personID)
                                }
                              />
                            }
                            key={student.personID}
                            label={formatFullName(
                              student.firstName,
                              student.lastName,
                            )}
                          />
                        ))}
                        {hasMoreStudents && (
                          <div
                            style={{
                              gridColumn: "span 2",
                              textAlign: "center",
                            }}
                          >
                            <button
                              className="btn custom-btn btn-small btn-light"
                              onClick={() => setStudentsPage((p) => p + 1)}
                              type="button"
                            >
                              Load more (
                              {students.length - paginatedStudents.length}{" "}
                              remaining)
                            </button>
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}
            </div>
          </>
        )}

        <div className="text-right">
          {loading ? (
            <div className="modal-btns-loader">
              <Loader />
            </div>
          ) : (
            <>
              <button
                className="btn link-text uppercase-text"
                onClick={onClose}
              >
                Cancel
              </button>
              <button
                className="btn link-text uppercase-text ml-24"
                disabled={!newLocation || selectedShliachPersonIds.length === 0}
                onClick={onSubmit}
              >
                Enroll
              </button>
            </>
          )}
          {errorMessage && <p className="error-text">{errorMessage}</p>}
        </div>
      </div>
    </Modal>
  );
}
